/**
 * Phase 7 Slice B — CampaignCockpitDetail page.
 *
 * Drill-down view at /app/cockpit/campaigns/:id. Reuses the existing
 * `cockpit-summary` endpoint scoped by `campaign_id` for the KPI tiles +
 * funnel, plus `ad-spend/summary?group_by=day` for the per-day spend
 * timeline, plus `crm/revenue/events?campaign_id=...` for the
 * revenue-events table. See ADR-0034.
 */
import React, { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import AuditHistoryLink from "../../components/app/AuditHistoryLink";
import CampaignPublishPanel from "../../components/app/CampaignPublishPanel";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, LineChart } from "lucide-react";
import { api } from "../../lib/api";
import { useT, useLocale } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import PeriodSelector, { rangeForPreset } from "../../components/app/PeriodSelector";

function fmtEur(n) {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("de-DE", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(n);
}
function fmtRoas(n) {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(2)}×`;
}
function fmtDate(s, locale) {
  if (!s) return "";
  return new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-GB",
        { day: "2-digit", month: "short", year: "numeric" })
      .format(new Date(s));
}

function DeltaPill({ pct, testid }) {
  if (pct === null || pct === undefined || !isFinite(pct)) return null;
  const positive = pct >= 0;
  const cls = positive
    ? "bg-emerald-100 text-emerald-800"
    : "bg-rose-100 text-rose-800";
  const arrow = positive ? "▲" : "▼";
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-mono ${cls}`}
    >
      {arrow}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function KpiTile({ label, value, fmt, testid, delta }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="mt-2 flex items-baseline justify-between gap-2">
          <div className="text-2xl font-semibold tabular-nums"
               data-testid={`${testid}-value`}>
            {fmt ? fmt(value) : (value ?? "—")}
          </div>
          <DeltaPill pct={delta} testid={`${testid}-delta`} />
        </div>
      </CardContent>
    </Card>
  );
}

export default function CampaignCockpitDetail() {
  const { id: campaignId } = useParams();
  const t = useT();
  const { locale } = useLocale();
  const [period, setPeriod] = useState(() => ({
    mode: "preset", preset: "365d", ...rangeForPreset("365d"),
  }));

  const cockpit = useQuery({
    queryKey: ["cockpit-summary", period.since, period.until, campaignId],
    queryFn: async () => {
      const r = await api.get("/crm/revenue/cockpit-summary", {
        params: { since: period.since, until: period.until,
                   campaign_id: campaignId },
      });
      return r.data;
    },
  });

  // Phase 14b — previous-period totals for WoW delta pills.
  const prevRange = useMemo(() => {
    try {
      const sinceMs = new Date(period.since).getTime();
      const untilMs = new Date(period.until).getTime();
      const len = Math.max(1, untilMs - sinceMs);
      return {
        since: new Date(sinceMs - len).toISOString(),
        until: new Date(sinceMs).toISOString(),
      };
    } catch (e) {
      return null;
    }
  }, [period.since, period.until]);

  const prevCockpit = useQuery({
    queryKey: ["cockpit-summary-prev", prevRange?.since, prevRange?.until,
                 campaignId],
    enabled: !!prevRange,
    queryFn: async () => {
      const r = await api.get("/crm/revenue/cockpit-summary", {
        params: { since: prevRange.since, until: prevRange.until,
                   campaign_id: campaignId },
      });
      return r.data;
    },
  });

  const spendByDay = useQuery({
    queryKey: ["ad-spend-by-day", period.since, period.until, campaignId],
    queryFn: async () => {
      const r = await api.get("/ad-spend/summary", {
        params: { since: period.since.slice(0, 10),
                   until: period.until.slice(0, 10),
                   campaign_id: campaignId, group_by: "day" },
      });
      return r.data;
    },
  });

  const events = useQuery({
    queryKey: ["revenue-events", period.since, period.until, campaignId],
    queryFn: async () => {
      const r = await api.get("/crm/revenue/events", {
        params: { since: period.since, until: period.until,
                   campaign_id: campaignId, limit: 20 },
      });
      return r.data;
    },
  });

  const totals = cockpit.data?.totals || {};
  const prevTotals = prevCockpit.data?.totals || {};
  const funnel = cockpit.data?.funnel || [];
  const campRow = (cockpit.data?.by_campaign || [])
    .find((c) => c.campaign_id === campaignId);
  const campaignName = campRow?.campaign_name || campaignId;

  // Phase 14b — WoW delta helper. Returns null if no meaningful comparison.
  function pctDelta(curr, prev) {
    const c = Number(curr);
    const p = Number(prev);
    if (!isFinite(c) || !isFinite(p) || p === 0) return null;
    return ((c - p) / Math.abs(p)) * 100;
  }
  const deltas = {
    revenue: pctDelta(totals.revenue_eur, prevTotals.revenue_eur),
    profit: pctDelta(totals.gross_profit_eur, prevTotals.gross_profit_eur),
    ad_spend: pctDelta(totals.total_ad_spend_eur, prevTotals.total_ad_spend_eur),
    roas: pctDelta(totals.roas, prevTotals.roas),
    cpl: pctDelta(totals.cpl_eur, prevTotals.cpl_eur),
    sales: pctDelta(totals.sale_count, prevTotals.sale_count),
  };

  const dailyRows = spendByDay.data?.daily || [];
  const maxDailySpend = useMemo(() =>
    Math.max(1, ...dailyRows.map((r) => r.total_spend_eur || 0)),
    [dailyRows]);

  const maxFunnel = Math.max(1, ...funnel.map((s) => s.count || 0));

  if (cockpit.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground"
                data-testid="campaign-detail-loading">
      {t("cockpit.loading", "Lade…")}
    </div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="campaign-detail-page">
      {/* Breadcrumb / header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/app/cockpit"
                className="inline-flex items-center text-sm text-muted-foreground hover:underline"
                data-testid="campaign-detail-back">
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t("cockpit.title", "Marktintelligenz")}
          </Link>
          <h1 className="mt-1 text-2xl font-bold tracking-tight inline-flex items-center gap-2">
            <LineChart className="h-6 w-6" />
            <span data-testid="campaign-detail-name">{campaignName}</span>
          </h1>
          <div className="mt-1 text-xs text-muted-foreground flex items-center gap-3">
            <span data-testid="campaign-detail-id">{campaignId}</span>
            <AuditHistoryLink kind="campaign" id={campaignId}
                              testid="campaign-audit-history-link" />
          </div>
        </div>
        <PeriodSelector value={period} onChange={setPeriod} locale={locale} />
      </div>

      {/* Phase 12b2 — Publish + performance panel */}
      <CampaignPublishPanel campaignId={campaignId} locale={locale} />

      {/* 6 KPI tiles scoped to this campaign */}
      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
               data-testid="campaign-detail-kpis">
        <KpiTile label={t("cockpit.kpi_revenue", "Umsatz")}
                 value={totals.revenue_eur} fmt={fmtEur}
                 testid="kpi-revenue" delta={deltas.revenue} />
        <KpiTile label={t("cockpit.kpi_profit", "Bruttogewinn")}
                 value={totals.gross_profit_eur} fmt={fmtEur}
                 testid="kpi-profit" delta={deltas.profit} />
        <KpiTile label={t("cockpit.kpi_ad_spend", "Werbeausgaben")}
                 value={totals.total_ad_spend_eur} fmt={fmtEur}
                 testid="kpi-ad-spend" delta={deltas.ad_spend} />
        <KpiTile label="ROAS" value={totals.roas} fmt={fmtRoas}
                 testid="kpi-roas" delta={deltas.roas} />
        <KpiTile label="CPL" value={totals.cpl_eur} fmt={fmtEur}
                 testid="kpi-cpl" delta={deltas.cpl} />
        <KpiTile label={t("cockpit.kpi_sales", "Verkäufe")}
                 value={totals.sale_count}
                 testid="kpi-sales" delta={deltas.sales} />
      </section>

      {/* Funnel */}
      <Card data-testid="campaign-detail-funnel">
        <CardHeader><CardTitle>{t("cockpit.funnel_title", "Lifecycle-Funnel")}</CardTitle></CardHeader>
        <CardContent>
          {funnel.length === 0 ? (
            <div className="text-sm text-muted-foreground"
                 data-testid="campaign-detail-funnel-empty">
              {t("cockpit.no_data", "Keine Daten im gewählten Zeitraum.")}
            </div>
          ) : funnel.map((s) => (
            <div key={s.stage} className="mb-2"
                 data-testid={`funnel-${s.stage}`}>
              <div className="flex items-baseline justify-between text-xs">
                <span className="font-medium">{s.stage}</span>
                <span className="tabular-nums">{s.count}</span>
              </div>
              <div className="h-2 rounded bg-muted overflow-hidden mt-1">
                <div className="h-full bg-primary"
                     style={{ width: `${(s.count / maxFunnel) * 100}%` }} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Ad-spend timeline */}
      <Card data-testid="campaign-detail-spend-timeline">
        <CardHeader>
          <CardTitle>{t("campaign_detail.spend_timeline",
                "Werbeausgaben über Zeit")}</CardTitle>
        </CardHeader>
        <CardContent>
          {dailyRows.length === 0 ? (
            <div className="text-sm text-muted-foreground"
                 data-testid="campaign-detail-spend-empty">
              {t("cockpit.no_data", "Keine Daten im gewählten Zeitraum.")}
            </div>
          ) : (
            <div className="flex items-end gap-1 h-48 overflow-x-auto"
                 data-testid="campaign-detail-spend-bars">
              {dailyRows.map((d) => (
                <div key={d.date}
                     className="flex flex-col items-center min-w-[28px]"
                     data-testid={`spend-bar-${d.date}`}>
                  <div title={`${d.date}: ${fmtEur(d.total_spend_eur)}`}
                       className="w-5 bg-primary/70 hover:bg-primary rounded-t"
                       style={{ height: `${Math.max(4,
                          (d.total_spend_eur / maxDailySpend) * 160)}px` }} />
                  <div className="text-[10px] text-muted-foreground mt-1 rotate-45 origin-left whitespace-nowrap">
                    {d.date.slice(5)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revenue events timeline */}
      <Card data-testid="campaign-detail-revenue-events">
        <CardHeader>
          <CardTitle>{t("campaign_detail.revenue_events",
                "Letzte Revenue-Events")}</CardTitle>
        </CardHeader>
        <CardContent>
          {(events.data?.items || []).length === 0 ? (
            <div className="text-sm text-muted-foreground"
                 data-testid="campaign-detail-events-empty">
              {t("cockpit.no_data", "Keine Daten im gewählten Zeitraum.")}
            </div>
          ) : (
            <table className="w-full text-sm"
                   data-testid="campaign-detail-events-table">
              <thead>
                <tr className="text-left text-xs uppercase text-muted-foreground border-b">
                  <th className="py-2 pr-3">{t("campaign_detail.col_when", "Wann")}</th>
                  <th className="py-2 pr-3">{t("campaign_detail.col_kind", "Ereignis")}</th>
                  <th className="py-2 pr-3 text-right">{t("cockpit.col_revenue", "Umsatz")}</th>
                  <th className="py-2 pr-3">{t("campaign_detail.col_lead", "Lead")}</th>
                </tr>
              </thead>
              <tbody>
                {(events.data?.items || []).map((e) => (
                  <tr key={e.id}
                      className="border-b last:border-0"
                      data-testid={`revenue-event-${e.id}`}>
                    <td className="py-2 pr-3 tabular-nums whitespace-nowrap">
                      {fmtDate(e.occurred_at, locale)}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge variant="secondary">{e.event_kind}</Badge>
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {fmtEur(e.payload?.revenue_eur)}
                    </td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground truncate max-w-[200px]">
                      {e.attribution?.lead_id || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
